import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();
const API_BASE = process.env.REACT_APP_USER_SERVICE_URL;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  /* ---------------- INIT FROM LOCAL STORAGE ---------------- */
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userId = localStorage.getItem("authUserId");
    const email = localStorage.getItem("authEmail");
    const phone = localStorage.getItem("authPhone");
    const firstName = localStorage.getItem("authFirstName");
    const roles = localStorage.getItem("authRoles");

    if (token && userId) {
      setAuth({
        userId,
        email,
        phone,
        firstName,
        token,
        roles: roles ? roles.split(",") : [],
      });
    }

    setLoading(false);
  }, []);

  /* ---------------- LOGIN (CALLED BY LOGIN PAGE) ---------------- */
  const login = (authData) => {
    localStorage.setItem("authToken", authData.token);
    localStorage.setItem("authUserId", authData.userId);
    localStorage.setItem("authEmail", authData.email);
    localStorage.setItem("authPhone", authData.phone || "");
    localStorage.setItem("authFirstName", authData.firstName || "");
    localStorage.setItem("authRoles", authData.roles.join(","));

    setAuth(authData);
  };

  /* ---------------- LOGOUT ---------------- */
  const logout = async (callBackend = true) => {
    if (loggingOut) return;
    setLoggingOut(true);

    const token = localStorage.getItem("authToken");

    if (callBackend && token) {
      try {
        await fetch(`${API_BASE}/users/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch {
        // ignore backend failure — logout is local-first
      }
    }

    localStorage.removeItem("authToken");
    localStorage.removeItem("authUserId");
    localStorage.removeItem("authEmail");
    localStorage.removeItem("authPhone");
    localStorage.removeItem("authFirstName");
    localStorage.removeItem("authRoles");

    setAuth(null);
    setLoggingOut(false);

    navigate("/login", { replace: true });
  };

  /* ---------------- AUTH FETCH (AUTO LOGOUT ON 401) ---------------- */
  const authFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem("authToken");

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      await logout(false);
      throw new Error("Unauthorized");
    }

    return response;
  };

  const isAuthenticated = !!auth?.token;

  return (
    <AuthContext.Provider
      value={{
        auth,
        isAuthenticated,
        login,
        logout,
        authFetch,
        loading,
        loggingOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
